import { useMemo } from 'react'
import { Ship, Home } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { useCltWizardStore } from '@/stores/useCltWizardStore'
import { CltStepHeader } from './CltStepHeader'
import { MatrixGrid } from './MatrixGrid'
import { collectInvalidRules, SUBSTEPS, type SubstepKey } from './clt.mock'

export function StepLogicMatrix() {
  const activeSubstep = useCltWizardStore(s => s.activeSubstep)
  const setActiveSubstep = useCltWizardStore(s => s.setActiveSubstep)
  const matrices = useCltWizardStore(s => s.matrices)

  const invalidCells = useMemo(() => collectInvalidRules(matrices), [matrices])
  const invalidCountBySubstep = useMemo(() => {
    const map: Partial<Record<SubstepKey, number>> = {}
    for (const cell of invalidCells) {
      map[cell.substepKey] = (map[cell.substepKey] ?? 0) + 1
    }
    return map
  }, [invalidCells])

  return (
    <div className="mx-auto w-full max-w-[1100px]">
      <CltStepHeader
        title="Milestone Logic"
        description="Define how each milestone's date is calculated for every Buy Strategy Group. Configure the four Import/Domestic × Rail/No-Rail matrices separately."
      />

      <Tabs
        value={activeSubstep}
        onValueChange={v => setActiveSubstep(v as SubstepKey)}
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <TabsList>
            {SUBSTEPS.map(sub => {
              const count = Object.keys(matrices[sub.key]).length
              const invalidCount = invalidCountBySubstep[sub.key] ?? 0
              const Icon = sub.group === 'Import' ? Ship : Home
              return (
                <TabsTrigger key={sub.key} value={sub.key}>
                  <Icon className="h-4 w-4" />
                  {sub.label}
                  {count > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-1.5 px-1.5 py-0 text-[11px]"
                    >
                      {count}
                    </Badge>
                  )}
                  {invalidCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="ml-1 px-1.5 py-0 text-[11px]"
                      title={`${invalidCount} invalid rule${invalidCount === 1 ? '' : 's'}`}
                    >
                      {invalidCount}
                    </Badge>
                  )}
                </TabsTrigger>
              )
            })}
          </TabsList>
          <p className="text-xs text-muted-foreground">
            Click a cell to set its rule, or use{' '}
            <span className="font-medium text-foreground">Fill</span> /{' '}
            <span className="font-medium text-foreground">Copy From</span> to
            populate a whole column.
          </p>
        </div>

        {SUBSTEPS.map(sub => (
          <TabsContent key={sub.key} value={sub.key}>
            <MatrixGrid substep={sub.key} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
